import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import {
  DollarSign,
  Percent,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  CheckCircle,
  X,
  Edit2,
} from 'lucide-react';

// ── Shared inline form ──
const ItemForm = ({ item, type, onSave, onCancel }) => {
  const isTax = type === 'tax';
  const [name, setName] = useState(item ? (isTax ? item.tax_name : item.fee_name) : (isTax ? 'VAT' : 'Service Fee'));
  const [value, setValue] = useState(item ? (isTax ? item.tax_value : item.fee_value) : '');
  const [kind, setKind] = useState(item ? (isTax ? item.tax_type : item.fee_type) : (isTax ? 'percentage' : 'amount'));
  const [active, setActive] = useState(item ? item.is_active : true);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || value === '' || value === null) return;
    const payload = isTax
      ? { tax_name: name, tax_value: parseFloat(value), tax_type: kind, is_active: active }
      : { fee_name: name, fee_value: parseFloat(value), fee_type: kind, is_active: active };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">Value</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">Type</label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="amount">Fixed Amount (£)</option>
            <option value="percentage">Percentage (%)</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">Status</label>
          <select
            value={active ? 'true' : 'false'}
            onChange={(e) => setActive(e.target.value === 'true')}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors">
          <X className="h-4 w-4 inline mr-1" />Cancel
        </button>
        <button type="submit" className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Save className="h-4 w-4 inline mr-1" />{item ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  );
};

// ── Section component (reused for Tax and Service Fee) ──
const Section = ({ title, icon: Icon, type, queryKey, apiBase }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const isTax = type === 'tax';

  const { data: items = [], isLoading, error } = useQuery(queryKey, async () => {
    const res = await api.get(`/vendor/${apiBase}`);
    return res.data.data;
  });

  const createMutation = useMutation(
    (payload) => api.post(`/vendor/${apiBase}`, payload),
    { onSuccess: () => { queryClient.invalidateQueries(queryKey); setShowForm(false); } }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => api.put(`/vendor/${apiBase}/${id}`, payload),
    { onSuccess: () => { queryClient.invalidateQueries(queryKey); setEditingItem(null); } }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/vendor/${apiBase}/${id}`),
    { onSuccess: () => queryClient.invalidateQueries(queryKey) }
  );

  const handleDelete = (id) => {
    if (window.confirm(`Delete this ${isTax ? 'tax' : 'service fee'}?`)) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
        <AlertCircle className="h-5 w-5 text-red-600" />
        <span className="text-red-800 text-sm">Failed to load {isTax ? 'taxes' : 'service fees'}</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-secondary-900 flex items-center space-x-2">
          <Icon className="h-5 w-5" />
          <span>{title}</span>
        </h2>
        {!showForm && !editingItem && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add</span>
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <ItemForm
          type={type}
          onSave={(payload) => createMutation.mutate(payload)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* List */}
      {items.length === 0 && !showForm ? (
        <p className="text-sm text-secondary-500 text-center py-6">No {isTax ? 'taxes' : 'service fees'} configured yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) =>
            editingItem === item.id ? (
              <ItemForm
                key={item.id}
                item={item}
                type={type}
                onSave={(payload) => updateMutation.mutate({ id: item.id, payload })}
                onCancel={() => setEditingItem(null)}
              />
            ) : (
              <div
                key={item.id}
                className="flex items-center justify-between border border-secondary-200 rounded-lg p-3 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className={`h-2.5 w-2.5 rounded-full ${item.is_active ? 'bg-green-500' : 'bg-secondary-300'}`} />
                  <div>
                    <span className="font-medium text-secondary-900 text-sm">
                      {isTax ? item.tax_name : item.fee_name}
                    </span>
                    <span className="ml-3 text-sm text-secondary-600">
                      {(isTax ? item.tax_type : item.fee_type) === 'percentage'
                        ? `${isTax ? item.tax_value : item.fee_value}%`
                        : `£${parseFloat(isTax ? item.tax_value : item.fee_value).toFixed(2)}`}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setEditingItem(item.id)}
                    className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ──
const TaxServiceFee = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-secondary-900 flex items-center space-x-2">
          <DollarSign className="h-6 w-6 md:h-7 md:w-7" />
          <span>Tax & Service Fee</span>
        </h1>
        <p className="text-sm md:text-base text-secondary-600 mt-1">
          Configure taxes and service fees applied to orders
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section
          title="Taxes"
          icon={Percent}
          type="tax"
          queryKey="vendor-taxes"
          apiBase="taxes"
        />
        <Section
          title="Service Fees"
          icon={DollarSign}
          type="fee"
          queryKey="vendor-service-fees"
          apiBase="service-fees"
        />
      </div>
    </div>
  );
};

export default TaxServiceFee;
